import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, type StaffUser, type TenantSummary } from './api.js';

/** Staff session. The tenant is implied by the account — never chosen in the UI. */

interface SessionValue {
  user: StaffUser | null;
  tenant: TenantSummary | null;
  loading: boolean;
  /**
   * Signed in on a password an administrator issued. The session can reach exactly one endpoint
   * until a new password is set, so the app shows the change-password screen and nothing else —
   * anything else would render a console whose every request comes back 403.
   */
  mustChangePassword: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  changePassword: (newPassword: string, currentPassword?: string) => Promise<void>;
  /**
   * Re-reads the signed-in account.
   *
   * The profile screen edits the same user the side nav renders, so without this the name
   * and picture in the corner stay as they were until the next full load — which reads as
   * the save not having worked.
   */
  refreshUser: () => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [tenant, setTenant] = useState<TenantSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const load = useCallback(async () => {
    if (!api.isAuthenticated()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const [me, tenantSummary] = await Promise.all([
        api.get<StaffUser>('/admin/auth/me'),
        api.get<TenantSummary>('/admin/tenant'),
      ]);
      setUser(me);
      setTenant(tenantSummary);
    } catch (error) {
      // A reload while on a temporary password lands here: /me is refused like everything else.
      // Recognising it keeps the change-password screen up instead of bouncing them to sign-in,
      // where the only password they have would take them straight back to this state.
      if ((error as { code?: string })?.code === 'PASSWORD_CHANGE_REQUIRED') {
        setMustChangePassword(true);
      } else {
        setUser(null);
        setTenant(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const onLost = () => {
      setUser(null);
      setTenant(null);
    };
    window.addEventListener('busbook:auth-lost', onLost);
    return () => window.removeEventListener('busbook:auth-lost', onLost);
  }, [load]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.request<{
      user: StaffUser;
      tokens: { accessToken: string; refreshToken: string };
      mustChangePassword?: boolean;
    }>('/admin/auth/login', {
      method: 'POST',
      auth: false,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    api.setTokens(result.tokens);
    setUser(result.user);

    if (result.mustChangePassword) {
      // Loading the tenant would be refused, and the failure would read as a broken sign-in
      // rather than as the one instruction this account has been given.
      setMustChangePassword(true);
      return;
    }
    setTenant(await api.get<TenantSummary>('/admin/tenant'));
  }, []);

  const changePassword = useCallback(async (newPassword: string, currentPassword?: string) => {
    const result = await api.post<{
      user: StaffUser;
      tokens: { accessToken: string; refreshToken: string };
    }>('/admin/auth/change-password', { newPassword, ...(currentPassword ? { currentPassword } : {}) });

    // Setting a password ends every session including this one, so the server hands back a fresh
    // pair. Storing them is what keeps the user signed in rather than dropped at the door.
    api.setTokens(result.tokens);
    setUser(result.user);
    setMustChangePassword(false);
    setTenant(await api.get<TenantSummary>('/admin/tenant'));
  }, []);

  const refreshUser = useCallback(async () => {
    setUser(await api.get<StaffUser>('/admin/auth/me'));
  }, []);

  const signOut = useCallback(() => {
    api.setTokens(null);
    setUser(null);
    setTenant(null);
    setMustChangePassword(false);
  }, []);

  return (
    <Ctx.Provider value={{ user, tenant, loading, mustChangePassword, signIn, changePassword, refreshUser, signOut }}>
      {children}
    </Ctx.Provider>
  );
}
